import type { IChannelPayloadPlaylistAssignment } from '../models/player-registration-payload'
import type { TFrame } from '../models/playlist'
import type { CampaignFrameSequenceAssignment, CampaignFrameSequenceRef } from './buildFrameSequenceRefs'
import { MARKETPLACE_PORTAL_IDLE_AD_ORDER_ID } from '@lucimark/shared/constants/marketplace'
import { normalizePlaylistRef } from './normalizePlaylistPayload'
import { isPortalFulfillmentPlaylistMetadata } from './portalFulfillmentPlaylist'

export type TPortalBookFrameSequencePlaylist = Pick<
  IChannelPayloadPlaylistAssignment,
  'playlistUrn' | 'frames' | 'metadata'
>

/** House row uses one embed placeholder with a capped frame count (not “all frames”). */
export function isPortalBookHouseEmbedSlot(frames: readonly TFrame[]): boolean {
  if (frames.length !== 1) {
    return false
  }
  const frame = frames[0]
  if (frame?.playlistRef == null) {
    return false
  }
  const ref = normalizePlaylistRef(frame.playlistRef) ?? frame.playlistRef
  return ref.frameCount != null
}

export function isPortalBookPaidOrderPlaylist(
  pl: Pick<IChannelPayloadPlaylistAssignment, 'metadata'>
): boolean {
  const meta = pl.metadata
  if (!isPortalFulfillmentPlaylistMetadata(meta)) {
    return false
  }
  return meta.portalAdOrderId !== MARKETPLACE_PORTAL_IDLE_AD_ORDER_ID
}

function pushHouseSlot(refs: CampaignFrameSequenceRef[], houseUrn: string): void {
  refs.push({ urn: houseUrn, frameIndex: 0 })
}

/**
 * Lucimark Book service-list playback when house embed caps frame count and at least one paid
 * order is active. Each loop is one or more blocks of [house visit, all paid orders in order].
 * Partial picks repeat for every rotating anchor in the pool; full-pool picks use one house visit
 * then all ads. Each house visit respects embed `frameCount` ({@link selectEmbeddedFrames}).
 */
export function buildPortalBookInterleavedFrameSequenceRefs(params: {
  assignments: readonly CampaignFrameSequenceAssignment[]
  playlists: readonly TPortalBookFrameSequencePlaylist[]
  /** Active editorial frames in the embedded house playlist (full-pool comparison). */
  houseEmbedPoolFrameCount: number
  /** Rotating pick units in the pool (bond heads + standalone frames; drives partial house block count). */
  houseEmbedPoolAnchorCount: number
}): CampaignFrameSequenceRef[] | null {
  if (params.playlists.length < 2 || params.assignments.length < 2) {
    return null
  }

  const housePlaylistIndex = params.playlists.findIndex(p =>
    isPortalBookHouseEmbedSlot(p.frames ?? [])
  )
  if (housePlaylistIndex < 0) {
    return null
  }
  const housePlaylist = params.playlists[housePlaylistIndex]!
  const houseFrames = housePlaylist.frames ?? []
  const houseUrn = housePlaylist.playlistUrn
  const embedRef = normalizePlaylistRef(houseFrames[0]!.playlistRef!) ?? houseFrames[0]!.playlistRef!
  if ((embedRef.frameCount ?? 0) <= 0) {
    return null
  }

  const poolFrameCount = Math.max(0, params.houseEmbedPoolFrameCount)
  const poolAnchorCount = Math.max(0, params.houseEmbedPoolAnchorCount)
  if (poolFrameCount <= 0 || poolAnchorCount <= 0) {
    return null
  }

  const adPlaylists = params.playlists.filter((p, i) =>
    i !== housePlaylistIndex && isPortalBookPaidOrderPlaylist(p)
  )
  if (adPlaylists.length < 1) {
    return null
  }

  const framesPerHouseVisit = embedRef.frameCount ?? poolFrameCount
  const playsFullHousePoolPerVisit = framesPerHouseVisit >= poolFrameCount
  const houseBlockCount = playsFullHousePoolPerVisit ? 1 : poolAnchorCount

  const refs: CampaignFrameSequenceRef[] = []

  for (let block = 0; block < houseBlockCount; block++) {
    pushHouseSlot(refs, houseUrn)
    for (const ad of adPlaylists) {
      /** One spine slot per paid order; {@link resolvePlaybackSegments} expands all book CPS rows. */
      refs.push({ urn: ad.playlistUrn, frameIndex: 0 })
    }
  }

  return refs
}
